<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <h2 class="text-3xl font-bold text-gray-900 dark:text-white">{{ $t('auth.create_account') }}</h2>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {{ $t('auth.welcome_text') }}
      </p>
    </div>

    <!-- Registration form -->
    <form @submit.prevent="handleRegister" class="space-y-5">
      <!-- Name fields -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label for="first_name" class="form-label text-gray-700 dark:text-gray-300">
            {{ $t('auth.first_name') }}
          </label>
          <input
            id="first_name"
            v-model="form.first_name"
            type="text"
            required
            class="form-input bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-500 focus:ring-red-500': errors.first_name }"
            placeholder="John"
          />
          <p v-if="errors.first_name" class="mt-1 text-sm text-red-500">{{ errors.first_name[0] }}</p>
        </div>

        <div>
          <label for="last_name" class="form-label text-gray-700 dark:text-gray-300">
            {{ $t('auth.last_name') }}
          </label>
          <input
            id="last_name"
            v-model="form.last_name"
            type="text"
            required
            class="form-input bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-500 focus:ring-red-500': errors.last_name }"
            placeholder="Doe"
          />
          <p v-if="errors.last_name" class="mt-1 text-sm text-red-500">{{ errors.last_name[0] }}</p>
        </div>
      </div>

      <!-- Email -->
      <div>
        <label for="email" class="form-label text-gray-700 dark:text-gray-300">
          {{ $t('auth.email') }}
        </label>
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <EnvelopeIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            class="form-input pl-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-500 focus:ring-red-500': errors.email }"
            placeholder="you@example.com"
          />
        </div>
        <p v-if="errors.email" class="mt-1 text-sm text-red-500">{{ errors.email[0] }}</p>
      </div>

      <!-- Password -->
      <div>
        <label for="password" class="form-label text-gray-700 dark:text-gray-300">
          {{ $t('auth.password') }}
        </label>
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <LockClosedIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            id="password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            required
            class="form-input pl-10 pr-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-500 focus:ring-red-500': errors.password }"
            placeholder="••••••••"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <EyeIcon v-if="!showPassword" class="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            <EyeSlashIcon v-else class="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        </div>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ $t('auth.password_min_length') }}</p>
        <p v-if="errors.password" class="mt-1 text-sm text-red-500">{{ errors.password[0] }}</p>
      </div>

      <!-- Password confirmation -->
      <div>
        <label for="password_confirmation" class="form-label text-gray-700 dark:text-gray-300">
          {{ $t('auth.confirm_password') }}
        </label>
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <LockClosedIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            id="password_confirmation"
            v-model="form.password_confirmation"
            :type="showPasswordConfirm ? 'text' : 'password'"
            required
            class="form-input pl-10 pr-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            placeholder="••••••••"
          />
          <button
            type="button"
            @click="showPasswordConfirm = !showPasswordConfirm"
            class="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <EyeIcon v-if="!showPasswordConfirm" class="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            <EyeSlashIcon v-else class="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        </div>
      </div>

      <!-- Terms and conditions -->
      <div class="flex items-start">
        <input
          id="terms"
          v-model="form.terms"
          type="checkbox"
          required
          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 mt-0.5"
        />
        <label for="terms" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
          {{ $t('auth.i_agree_to') }}
          <a href="#" class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300">{{ $t('settings.terms') }}</a>
          {{ $t('auth.and') }}
          <a href="#" class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300">{{ $t('settings.privacy') }}</a>
        </label>
      </div>

      <!-- Submit button -->
      <button
        type="submit"
        :disabled="isLoading || !form.terms"
        class="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
      >
        <div v-if="isLoading" class="flex items-center justify-center">
          <div class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          {{ $t('auth.creating_account') }}
        </div>
        <span v-else class="flex items-center justify-center">
          {{ $t('auth.create_account_button') }}
          <SparklesIcon class="ml-2 h-4 w-4" />
        </span>
      </button>

      <!-- Features list -->
      <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-3 font-medium uppercase tracking-wider">{{ $t('auth.account_includes') }}</p>
        <div class="grid grid-cols-2 gap-2 text-xs text-gray-700 dark:text-gray-300">
          <div class="flex items-center">
            <CheckIcon class="h-4 w-4 text-green-500 mr-1.5" />
            <span>{{ $t('auth.emails_per_month') }}</span>
          </div>
          <div class="flex items-center">
            <CheckIcon class="h-4 w-4 text-green-500 mr-1.5" />
            <span>{{ $t('auth.unlimited_contacts') }}</span>
          </div>
          <div class="flex items-center">
            <CheckIcon class="h-4 w-4 text-green-500 mr-1.5" />
            <span>{{ $t('auth.email_automation') }}</span>
          </div>
          <div class="flex items-center">
            <CheckIcon class="h-4 w-4 text-green-500 mr-1.5" />
            <span>{{ $t('auth.support_24_7') }}</span>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  SparklesIcon,
  CheckIcon
} from '@heroicons/vue/24/outline'

const { t } = useI18n()

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

// Get current locale for links
const currentLocale = route.params.locale || 'en'

const form = reactive({
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password_confirmation: '',
  terms: false
})

const errors = ref({})
const isLoading = ref(false)
const showPassword = ref(false)
const showPasswordConfirm = ref(false)

const handleRegister = async () => {
  if (form.password !== form.password_confirmation) {
    toast.error(t('common.validation.password_mismatch'))
    return
  }

  isLoading.value = true
  errors.value = {}

  try {
    // authStore.register handles redirect with locale automatically
    await authStore.register(form)
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors || {}
    } else {
      toast.error(t('common.messages.error'))
    }
  } finally {
    isLoading.value = false
  }
}
</script>
